import { useEffect, useRef } from 'react';
import { endAccessSession } from '../accessSession';
import { useUserStore } from '@/entities/user/store';

/**
 * How long an unattended session stays usable.
 *
 * Counted from the last thing the user DID, not from sign-in. That is the
 * distinction that makes this worth having: the risk a session timeout
 * addresses is an unlocked machine nobody is sitting at, and an absolute
 * timeout does not address it any better while also ending the session of
 * somebody in the middle of their work. An hour of no input is a strong signal
 * that nobody is there; an hour of continuous use is a signal that somebody is.
 *
 * The ABSOLUTE bound is separate and lives on the server, against the ID
 * token's signed `auth_time` (Settings.session_max_age_hours). Two limits, both
 * doing something: this one ends a session nobody is using, that one guarantees
 * a re-authentication even for a tab that is never left alone.
 */
const IDLE_LIMIT_MS = 60 * 60 * 1000;

/** How often we check. Coarse on purpose -- see the note in the hook. */
const CHECK_INTERVAL_MS = 60 * 1000;

/**
 * The events that count as "somebody is here".
 *
 * Deliberately not `mousemove`: a sleeping laptop nudged by a passing cable, or
 * a page with any animation under the cursor, can emit it without a person. All
 * four of these require an intent.
 */
const ACTIVITY_EVENTS = [
  'pointerdown',
  'keydown',
  'wheel',
  'touchstart',
] as const;

export interface IdleSignOutOptions {
  /**
   * Which client-side session flag to clear. Defaults to the advisor's
   * `useUserStore`.
   *
   * The two populations have SEPARATE stores on purpose, and `portalSession`
   * says why: `resetAllStores()` on advisor logout would clear a shared one,
   * so an advisor signing out in one tab would sign a client out in another —
   * and a portal sign-in that set the advisor's flag would walk a client into
   * the protected tree. So a hook that always cleared `useUserStore` could
   * only ever end the wrong session on the portal: it would clear a store the
   * client never populated and leave the one that actually gates them alone.
   */
  clearSession?: () => void;
  /**
   * Where to land afterwards. Defaults to the advisor's `/login`.
   *
   * A client sent to `/login` arrives at a screen offering Sign Up and Google
   * SSO for a product they have no account in, having been signed out of one
   * they do.
   */
  signInPath?: string;
}

/**
 * Sign the user out after an hour with no input.
 *
 * WHY THIS EXISTS. Firebase's refresh token does not expire, and the SDK
 * renews the ID token roughly hourly for as long as the tab is open. So a
 * signed-in tab stayed signed in indefinitely -- overnight, over a weekend, on
 * a shared or stolen machine -- and nothing in the product ended it. The
 * hourly ID-token expiry looks like a limit and is not one; it is refreshed
 * before it ever bites.
 *
 * Mounted inside the authenticated layout, so it exists only while there is a
 * session to end.
 *
 * The check is a one-minute poll rather than a timer armed per activity event.
 * A `setTimeout` reset on every keystroke re-arms thousands of times during
 * normal typing, and a timer is not honest about a suspended machine either:
 * `setTimeout` does not fire while a laptop is asleep, so a lid closed at 09:00
 * and opened at 17:00 would find its timeout still pending. Comparing
 * timestamps on a poll gets the sleep case right, because the clock moved even
 * though nothing ran. One minute of slack on an hour-long window is not worth
 * more machinery than that.
 */
export function useIdleSignOut(options: IdleSignOutOptions = {}): void {
  // The advisor's store and the advisor's login, which is what every existing
  // caller wants and what this hook did unconditionally. `useUserStore` is
  // subscribed to either way — hooks cannot be called conditionally — and the
  // selector is cheap; only the value used is chosen.
  const clearAdvisorSession = useUserStore((state) => state.logout);
  const clearSession = options.clearSession ?? clearAdvisorSession;
  const signInPath = options.signInPath ?? '/login';
  const lastActivity = useRef(Date.now());
  // Guards against a second sign-out while the first is in flight: the poll
  // keeps firing until the navigation actually happens.
  const signingOut = useRef(false);

  useEffect(() => {
    const markActive = () => {
      lastActivity.current = Date.now();
    };

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, markActive, { passive: true });
    }

    /**
     * Clear the server session, then the client one, then hard-navigate.
     *
     * The local clear happens on failure too, and that ordering is the point:
     * being unable to reach /auth/logout must not mean being unable to end the
     * session. A hard navigation rather than a router push so nothing
     * rehydrates from memory. Same shape as the sign-out on the two-factor
     * gate, for the same reasons.
     */
    const signOut = () => {
      if (signingOut.current) return;
      signingOut.current = true;
      const finish = () => {
        clearSession();
        window.location.href = signInPath;
      };
      void endAccessSession().then(finish, finish);
    };

    const timer = window.setInterval(() => {
      if (Date.now() - lastActivity.current >= IDLE_LIMIT_MS) signOut();
    }, CHECK_INTERVAL_MS);

    return () => {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, markActive);
      }
      window.clearInterval(timer);
    };
  }, [clearSession, signInPath]);
}
